import React from 'react';
import { Send as ResendIcon, Delete as CancelIcon, Check as AcceptIcon, Close as DeclineIcon } from '@mui/icons-material';
import { Chit, ChitContent, ChitMain, ChitAvatar, ChitText, ChitStatus, ChitActions, ChitActionButton } from '@/components/ui/chit';
import { getRoleLabel, getStatusIcon, formatDate } from 'src/utils/organizationUtils';

interface InvitationCardProps {
  inviteId: string;
  invitedEmail: string;
  role: string;
  customRoleName?: string;
  invitedByName: string;
  status: string;
  createdDate: string;
  expiresDate?: string;
  orgName?: string; // For incoming invites
  isIncoming?: boolean; // Whether this is an incoming invite
  onResend?: (inviteId: string) => void;
  onCancel?: (inviteId: string) => void;
  onAccept?: (inviteId: string) => void;
  onDecline?: (inviteId: string) => void;
}

export const InvitationCard: React.FC<InvitationCardProps> = ({
  inviteId,
  invitedEmail,
  role,
  customRoleName,
  invitedByName,
  status,
  createdDate,
  expiresDate,
  orgName,
  isIncoming = false,
  onResend,
  onCancel,
  onAccept,
  onDecline
}) => {

  const isExpiringSoon = (expiresDate?: string) => {
    if (!expiresDate) return false;
    const expires = new Date(expiresDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  const expiringSoon = isExpiringSoon(expiresDate);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'declined': 
      case 'expired': 
        return 'error';
      default: return 'default';
    }
  };

  const StatusIcon = getStatusIcon(status);
  const subtitle = (
    <span className="flex items-center gap-2">
      <ChitStatus variant={getStatusVariant(status)} icon={StatusIcon}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </ChitStatus>
      <span className="text-white/20">•</span>
      <span>Role: {getRoleLabel(role, customRoleName)}</span>
    </span>
  );

  const description = isIncoming ? (
    <span>
      <span>Invited by {invitedByName} {orgName && `to join ${orgName}`} on {formatDate(createdDate)}</span>
      {status === 'pending' && expiresDate && (
        <>
          <span className="text-white/20"> • </span>
          <span className={expiringSoon ? 'text-yellow-400' : 'text-white/30'}>
            Expires {formatDate(expiresDate)}
            {expiringSoon && ' (Soon)'}
          </span>
        </>
      )}
    </span>
  ) : (
    <span>
      <span>Invited by {invitedByName} on {formatDate(createdDate)}</span>
      {status === 'pending' && expiresDate && (
        <>
          <span className="text-white/20"> • </span>
          <span className={expiringSoon ? 'text-yellow-400' : 'text-white/30'}>
            Expires {formatDate(expiresDate)}
            {expiringSoon && ' (Soon)'}
          </span>
        </>
      )}
    </span>
  );

  const chitVariant = expiringSoon && status === 'pending' ? 'warning' : 'default';

  return (
    <Chit variant={chitVariant} interactive={false}>
      <ChitContent>
        <ChitMain>
          <ChitAvatar fallback={invitedEmail.substring(0, 2).toUpperCase()} size="md" />
          <ChitText 
            title={invitedEmail}
            subtitle={subtitle}
            description={description}
            titleClassName="text-base"
            subtitleClassName="text-white/40 text-sm"
            descriptionClassName="text-white/30 text-xs"
          />
        </ChitMain>
        
        {status === 'pending' && (
          <ChitActions>
            {isIncoming ? (
              <>
                {onAccept && (
                  <ChitActionButton
                    onClick={() => onAccept(inviteId)}
                    variant="success"
                    size="sm"
                    icon={<AcceptIcon />}
                  >
                    Accept
                  </ChitActionButton>
                )}
                {onDecline && (
                  <ChitActionButton
                    onClick={() => onDecline(inviteId)}
                    variant="error"
                    size="sm"
                    icon={<DeclineIcon />}
                  >
                    Decline
                  </ChitActionButton>
                )}
              </>
            ) : (
              <>
                {onResend && (
                  <ChitActionButton
                    onClick={() => onResend(inviteId)}
                    variant="success"
                    size="sm"
                    icon={<ResendIcon />}
                  >
                    Resend
                  </ChitActionButton>
                )}
                {onCancel && (
                  <ChitActionButton
                    onClick={() => onCancel(inviteId)}
                    variant="error"
                    size="sm"
                    icon={<CancelIcon />}
                  >
                    Cancel
                  </ChitActionButton>
                )}
              </>
            )}
          </ChitActions>
        )}
      </ChitContent>
    </Chit>
  );
};